/**
 * One unit: its details, leases, owners and files.
 *
 * Mirrors PropertyShow.tsx's shape — hero image, a details card, a files
 * section — scaled down to what a unit actually has, plus two read-only
 * jump-through panels: the leases (and tenants) that ran on this unit, and
 * the property's current owners. Neither is unit-scoped data in the schema —
 * a lease points at a unit directly, but ownership is a property-level
 * table (CLAUDE.md: "an ownership row is what lets an owner see a
 * property") — so the owners panel is explicitly labeled as coming from the
 * property, not invented as unit-level ownership that doesn't exist.
 *
 * Neither panel is capability-gated beyond being staff at all: `GET
 * /api/leases` and `GET /api/ownerships` are `app.authenticate`-only, same
 * as PropertyShow's own Ownership table already is for every staff role
 * including MAINTENANCE — RLS decides the rows, not a second capability
 * check layered on top of an inventory Show page.
 */

import { useInvalidate, useList, useOne } from '@refinedev/core';
import { Link, useNavigate, useParams } from 'react-router';
import { Avatar } from '../../../components/Avatar.js';
import { CoverImage } from '../../../components/CoverImage.js';
import { DataTable, type Column } from '../../../components/DataTable.js';
import { DocumentList } from '../../../components/DocumentList.js';
import { ListingComposer, type ListingSummary } from '../../../components/ListingComposer.js';
import { PhotoUpload } from '../../../components/PhotoUpload.js';
import { humanizeEnum } from '../../../components/Form.js';
import { useCan } from '../../../lib/capabilities.js';

interface UnitRecord {
  id: string;
  propertyId: string;
  propertyName?: string;
  label: string;
  bedrooms: number;
  bathrooms: string;
  squareFeet: number | null;
  status: string;
  marketRent: string;
  notes: string | null;
  photoUrl: string | null;
  listing: ListingSummary | null;
  archivedAt: string | null;
}

interface LeaseTenantSummary {
  tenantId: string;
  name: string;
  isPrimary: boolean;
  photoUrl: string | null;
}

interface LeaseRow {
  id: string;
  status: string;
  startDate: string | null;
  endDate: string | null;
  monthlyRent: string;
  tenants: LeaseTenantSummary[];
}

interface OwnershipRow {
  id: string;
  ownerId: string;
  ownerName?: string;
  ownerPhotoUrl?: string | null;
  percent: string;
  startDate: string | null;
  endDate: string | null;
  isCurrent: boolean;
}

export function UnitShow() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const invalidate = useInvalidate();

  // Same two-capability split as PropertyShow: reshaping inventory is
  // narrower than attaching a photo, which every staff role (including
  // MAINTENANCE) does as part of their actual job.
  const canEditInventory = useCan('inventory:write');
  const canWriteDocuments = useCan('documents:write');

  const { data, isLoading } = useOne<UnitRecord>({
    resource: 'units',
    id: id ?? '',
    queryOptions: { enabled: Boolean(id) },
  });

  const unit = data?.data;

  const { data: leasesData, isLoading: leasesLoading } = useList<LeaseRow>({
    resource: 'leases',
    filters: [{ field: 'unitId', operator: 'eq', value: unit?.id }],
    pagination: { mode: 'off' },
    queryOptions: { enabled: Boolean(unit) },
  });

  const { data: ownershipsData, isLoading: ownershipsLoading } = useList<OwnershipRow>({
    resource: 'ownerships',
    filters: [{ field: 'propertyId', operator: 'eq', value: unit?.propertyId }],
    pagination: { mode: 'off' },
    queryOptions: { enabled: Boolean(unit) },
  });

  if (isLoading) return <p className="muted">Loading…</p>;
  if (!unit) return <p className="alert alert--error">Unit not found.</p>;

  const leaseColumns: Column<LeaseRow>[] = [
    {
      key: 'tenants',
      header: 'Tenant',
      render: (row) =>
        row.tenants.length === 0 ? (
          '—'
        ) : (
          <div className="stack" style={{ gap: '0.35rem' }}>
            {row.tenants.map((tenant) => (
              <span key={tenant.tenantId} className="person-cell">
                <Avatar name={tenant.name} photoUrl={tenant.photoUrl} />
                <Link
                  to={`/staff/tenants/${tenant.tenantId}/edit`}
                  // The row itself jumps to the lease; this jumps to the
                  // tenant instead, so it must not also trigger the row's
                  // own click handler.
                  onClick={(event) => event.stopPropagation()}
                >
                  {tenant.name}
                </Link>
              </span>
            ))}
          </div>
        ),
    },
    {
      key: 'term',
      header: 'Term',
      render: (row) => (
        <span className="stack" style={{ gap: '0.2rem' }}>
          <span className="pill">{humanizeEnum(row.status)}</span>
          <span className="muted" style={{ fontSize: '0.82rem' }}>
            {row.startDate ?? '—'} – {row.endDate ?? 'current'}
          </span>
        </span>
      ),
    },
    { key: 'rent', header: 'Rent', numeric: true, render: (row) => `$${row.monthlyRent}` },
  ];

  const ownershipColumns: Column<OwnershipRow>[] = [
    {
      key: 'owner',
      header: 'Owner',
      render: (row) => (
        <span className="person-cell">
          <Avatar name={row.ownerName ?? row.ownerId} photoUrl={row.ownerPhotoUrl ?? null} />
          {row.ownerName ?? row.ownerId}
        </span>
      ),
    },
    { key: 'percent', header: 'Share', numeric: true, render: (row) => `${row.percent}%` },
    {
      key: 'term',
      header: 'Term',
      render: (row) => (
        <span className="muted" style={{ fontSize: '0.82rem' }}>
          {row.startDate ?? '—'} – {row.endDate ?? 'current'}
        </span>
      ),
    },
  ];

  return (
    <section className="stack">
      <div className="row">
        <h1>
          {unit.label}
          {unit.propertyName ? <span className="muted"> · {unit.propertyName}</span> : null}
        </h1>
        {unit.archivedAt ? <span className="pill">Archived</span> : null}
        <span className="shell__spacer" />
        <button
          type="button"
          className="button button--quiet"
          onClick={() => void navigate(`/staff/properties/${unit.propertyId}`)}
        >
          ← {unit.propertyName ?? 'Property'}
        </button>
        {canEditInventory ? (
          <button
            type="button"
            className="button"
            onClick={() => void navigate(`/staff/units/${unit.id}/edit`)}
          >
            Edit
          </button>
        ) : null}
      </div>

      <CoverImage
        src={unit.photoUrl}
        alt={`${unit.propertyName ?? ''} ${unit.label}`}
        variant="hero"
      />

      <div className="card stack">
        <div className="row">
          <span className="pill">{humanizeEnum(unit.status)}</span>
          <span className="muted">
            {unit.bedrooms} bed / {unit.bathrooms} bath
          </span>
          {unit.squareFeet !== null ? (
            <span className="muted">{unit.squareFeet.toLocaleString()} sq ft</span>
          ) : null}
          {/* Money as the server's own decimal string — never parsed to a number. */}
          <span className="muted">${unit.marketRent} market rent</span>
        </div>
        {unit.notes ? <p>{unit.notes}</p> : null}
      </div>

      <ListingComposer
        unitId={unit.id}
        listing={unit.listing}
        canEdit={canEditInventory}
        onChange={() => void invalidate({ resource: 'units', invalidates: ['detail'] })}
      />

      <div className="panel-grid">
        <section>
          <h2>Leases</h2>
          <DataTable
            columns={leaseColumns}
            rows={leasesData?.data ?? []}
            rowKey={(row) => row.id}
            isLoading={leasesLoading}
            onRowClick={(row) => void navigate(`/staff/leases/${row.id}`)}
            emptyMessage="No leases on record for this unit."
          />
        </section>

        <section>
          <h2>Owners</h2>
          <p className="muted"></p>
          <DataTable
            columns={ownershipColumns}
            rows={ownershipsData?.data ?? []}
            rowKey={(row) => row.id}
            isLoading={ownershipsLoading}
            onRowClick={(row) => void navigate(`/staff/owners/${row.ownerId}/edit`)}
            emptyMessage="No owner on record for this property."
          />
        </section>
      </div>

      <h2>Files</h2>
      <DocumentList filter={{ unitId: unit.id }} canDelete={canWriteDocuments} />
      {canWriteDocuments ? (
        <details>
          <summary>Upload a photo or document</summary>
          <div className="card">
            <PhotoUpload
              parent={{ unitId: unit.id }}
              defaultDocumentType="UNIT_PHOTO"
              onUploaded={() => {
                void invalidate({ resource: 'documents', invalidates: ['list'] });
                void invalidate({ resource: 'units', invalidates: ['detail'] });
              }}
            />
          </div>
        </details>
      ) : null}
    </section>
  );
}
